// The building blocks shared by the admin panel's Capability and Activity tabs,
// so the two read as one design: section 1 is a score card, section 2 a chart
// in the same frame, section 3 a titled list, with a line between sections.

import React from 'react';
import { Box, Typography } from '@mui/material';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import { fadeInSx } from '../../../../../../components/ui/DashboardSkeletons';
import { capabilityColor } from './teamCapability';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';

const FONT = 'Inter, sans-serif';
const BRANDY_FRAME = '1px solid rgba(247, 148, 32, 0.45)';
// A % that opens a tooltip: a subtle full underline instead of an (i) icon.
const HOVER_TARGET = {
  cursor: 'default',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(182, 182, 182, 0.45)',
  textDecorationThickness: '1px',
  textUnderlineOffset: '4px'
};

/** A tier name shown where a % would be, in the tier's colour. */
export interface TierText {
  text: string;
  color: string;
}

/** The line between a tab's sections. */
export const SectionDivide: React.FC = () => (
  <Box sx={{ flexShrink: 0, height: '1px', bgcolor: '#2A2A2A', my: '8px' }} />
);

const LINE_TEXT = { fontFamily: FONT, fontSize: 11.5, fontWeight: 600, lineHeight: 1.15, whiteSpace: 'nowrap' };
// The score card's right-hand grid: vertical lines only, no outline. Each cell
// carries its own border and padding, so the lines run unbroken through the rows.
const GRID_LINE = '1px solid #4A4A4A';
const cell = (column: number) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  py: '5px',
  pl: column === 0 ? 0 : '8px',
  pr: column === 2 ? 0 : '8px',
  ...(column > 0 ? { borderLeft: GRID_LINE } : {}),
  // Phone: the label on a line of its own, then the name (wrapping, never cut) and its %.
  [MOBILE_SX]:
    column === 0
      ? { gridColumn: '1 / -1', pt: '6px', pb: 0 }
      : { borderLeft: 'none', pt: '2px', pb: '6px', pl: column === 1 ? 0 : '10px', pr: 0 }
});
// A tap opens a tooltip on the phone (a long press otherwise), and it stays long enough to read.
const PHONE_TIP = { enterTouchDelay: 0, leaveTouchDelay: 8000 };
const PHONE_TIP_WIDTH = { [MOBILE_SX]: { maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh', overflowY: 'auto' } };

/**
 * No score yet: a sand timer. Its "Not yet" is the icon's own title (hover and screen readers), as the
 * figure it stands in for may already open a tooltip of its own.
 */
const NotYet: React.FC<{ size: number }> = ({ size }) => (
  <HourglassBottomIcon titleAccess="Not yet" sx={{ fontSize: size, color: '#868686', verticalAlign: 'middle' }} />
);

/**
 * One row of a score card's right-hand grid: label | name | %. The % is
 * colour-banded; with a tooltip it is underlined and opens it on hover.
 */
export const ScoreLine: React.FC<{
  label: string;
  name: string;
  percent: number | null;
  /** Shown in place of the % (a tier name), in its own colour. */
  tier?: TierText;
  tooltip?: React.ReactNode;
}> = ({ label, name, percent, tier, tooltip }) => {
  const isMobileApp = useIsMobileApp();
  const figure = (
    <Typography
      component="span"
      sx={{
        ...LINE_TEXT,
        ...(tooltip ? HOVER_TARGET : {}),
        fontWeight: 700,
        color: tier ? tier.color : capabilityColor(percent),
        ...(tier ? { textTransform: 'uppercase' } : {}),
        [MOBILE_SX]: { fontSize: 14 }
      }}
    >
      {tier ? tier.text : percent === null ? <NotYet size={14} /> : `${percent}%`}
    </Typography>
  );
  return (
    <>
      <Box sx={cell(0)}>
        <Typography sx={{ ...LINE_TEXT, color: '#B6B6B6', [MOBILE_SX]: { fontSize: 11, fontWeight: 500 } }}>
          {label}
        </Typography>
      </Box>
      <Box sx={cell(1)}>
        <Typography
          sx={{
            ...LINE_TEXT,
            color: '#FDFDFD',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [MOBILE_SX]: { fontSize: 13, lineHeight: 1.3, whiteSpace: 'normal', overflowWrap: 'break-word' }
          }}
        >
          {name}
        </Typography>
      </Box>
      <Box sx={cell(2)}>
        {tooltip ? (
          <DosenTooltip
            placement="bottom-start"
            componentsProps={{ tooltip: { sx: { maxWidth: 'none', ...PHONE_TIP_WIDTH } } }}
            title={tooltip}
            {...(isMobileApp ? PHONE_TIP : {})}
          >
            {figure}
          </DosenTooltip>
        ) : (
          figure
        )}
      </Box>
    </>
  );
};

/**
 * The headline tier's size: the short names (LOW, HIGH) stand tall; the long
 * ones (MODERATE, EXCELLENT, EXCEPTIONAL) step down so the card's right-hand
 * lines always keep their room, even at the panel's narrowest.
 */
const tierFontSize = (text: string): number => {
  if (text.length <= 4) return 34;
  if (text.length <= 8) return 22;
  return text.length <= 9 ? 20 : 17;
};

/**
 * Section 1: the headline at full height on the left (colour-banded,
 * underlined when it has a tooltip), ScoreLine rows on the right. The headline
 * is the % (Capability) or, given a tier, the tier's name alone (Activity; its
 * % is in the tooltip). Both cards are the same height. The same slim brandy frame and faint tint as the rows
 * further down.
 */
export const ScoreCard: React.FC<{
  percent: number | null;
  /** The tier: it becomes the headline, in place of the %. */
  tier?: TierText;
  tooltip?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ percent, tier, tooltip, children }) => {
  const isMobileApp = useIsMobileApp();
  const figure = tier ? (
    <Box
      sx={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '8px',
        pb: '6px',
        ...(tooltip ? { cursor: 'default', borderBottom: '2px solid rgba(182, 182, 182, 0.45)' } : {}),
        fontFamily: FONT,
        fontWeight: 700,
        lineHeight: 1,
        color: tier.color
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: tierFontSize(tier.text),
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap'
        }}
      >
        {tier.text}
      </Box>
    </Box>
  ) : (
    // A thicker underline with a little air above it; the % sign smaller and
    // centred on the number.
    <Box
      sx={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        pb: '6px',
        ...(tooltip ? { cursor: 'default', borderBottom: '2px solid rgba(182, 182, 182, 0.45)' } : {}),
        fontFamily: FONT,
        fontWeight: 700,
        lineHeight: 1,
        color: capabilityColor(percent)
      }}
    >
      <Box component="span" sx={{ fontSize: percent === null ? 24 : 56 }}>
        {percent === null ? <NotYet size={44} /> : percent}
      </Box>
      {percent !== null && (
        <Box component="span" sx={{ fontSize: 28 }}>
          %
        </Box>
      )}
    </Box>
  );
  const headline = (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
      {tooltip ? (
        <DosenTooltip
          placement="bottom-start"
          componentsProps={{ tooltip: { sx: { maxWidth: 460, ...PHONE_TIP_WIDTH } } }}
          title={tooltip}
          {...(isMobileApp ? PHONE_TIP : {})}
        >
          {figure}
        </DosenTooltip>
      ) : (
        figure
      )}
    </Box>
  );
  return (
    <Box
      sx={{
        flexShrink: 0,
        p: '16px 16px',
        // The height of the Capability card's 56px figure, whichever headline it carries.
        minHeight: 96,
        boxSizing: 'border-box',
        borderRadius: '4px',
        border: BRANDY_FRAME,
        bgcolor: 'rgba(247, 148, 32, 0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        ...fadeInSx,
        // Phone: the lines drop under the headline when a long one (a tier name) leaves them too little room.
        [MOBILE_SX]: { p: '12px 14px', gap: '10px 14px', flexWrap: 'wrap' }
      }}
    >
      {headline}
      {children && (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'grid',
            // Every column hugs its content and the block sits on the right, so the % follows the name directly.
            gridTemplateColumns: 'auto minmax(0, max-content) auto',
            alignItems: 'stretch',
            justifyContent: 'end',
            // Phone: name and % share a row under each label (see cell).
            [MOBILE_SX]: { flex: '1 1 176px', gridTemplateColumns: 'minmax(0, 1fr) auto', justifyContent: 'stretch' }
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

/** Section 2: the chart in the same brandy frame, no fill. */
export const ChartSection: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      flexShrink: 0,
      p: '12px 14px',
      borderRadius: '4px',
      border: BRANDY_FRAME,
      bgcolor: 'transparent',
      ...fadeInSx
    }}
  >
    {children}
  </Box>
);

/** Section 3's title: the same type as the row titles beneath it. */
export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      flexShrink: 0,
      fontFamily: FONT,
      fontSize: 18,
      fontWeight: 600,
      textTransform: 'uppercase',
      color: '#FDFDFD'
    }}
  >
    {children}
  </Typography>
);
